import type { VoidComponent } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import type { Id } from "@thisbeyond/solid-dnd";
import Moveable from "./Moveable";
import LastDropZone from "./LastDropZone";
import EmptyDropZone from "./EmptyDropZone";
import type { FieldConfig } from "../../../fields/types";
import { useFormEditorSidePanel } from "../FormEditorContent";
import { getPanelCloseButtonId } from "../../../components/panels/FormEditorSidePanel";
import type { FormScope } from "../../../state/formVersion";
import { useTranslation } from "../../../state/i18n";

type DroppableListProps = {
  header?: { title: string; description?: string };
  elements: FieldConfig[];
  id: Id;
  ignoredZones: Id[];
  currentScope: FormScope;
};

const DroppableList: VoidComponent<DroppableListProps> = (props) => {
  const { t } = useTranslation();
  const { selectedPanel, setSelectedPanel } = useFormEditorSidePanel();
  const isActive = createMemo(() => selectedPanel()?.type === "formDetails");

  return (
    <div class="flex size-full flex-col pr-9.5">
      <Show when={props.header}>
        {(header) => (
          <button
            id="form-details"
            class="mb-2 min-w-0 rounded-lg border border-subtle bg-surface-default p-8 text-left hover:bg-surface-hover focus-visible:outline-default"
            classList={{
              "shadow-elevation-light-1 hover:shadow-elevation-light-2": !isActive(),
              "shadow-elevation-light-2 outline-default": isActive(),
            }}
            onClick={() => {
              setSelectedPanel({ type: "formDetails" });
              const panelCloseButton = document.querySelector(
                `[data-id=${getPanelCloseButtonId(props.currentScope)}]`,
              ) as HTMLButtonElement;
              panelCloseButton?.focus();
            }}
            data-tour-highlight-id="form-header"
          >
            <span class="sr-only">{t(($) => $.FORM_EDITOR_FORM_DETAILS_EDIT_ARIA)}: </span>
            <h1 class="text-header-3xl wrap-break-word text-primary">{header().title}</h1>
            <Show when={header().description}>
              <p class="mt-1 text-medium-regular wrap-break-word text-secondary">{header().description}</p>
            </Show>
          </button>
        )}
      </Show>
      <Show when={props.elements.length > 0} fallback={<EmptyDropZone currentScope={props.currentScope} />}>
        <div class="flex flex-col gap-2">
          <For each={props.elements}>
            {(element) => (
              <div data-type="field" id={element.uid.toString()}>
                <Moveable ignoredZones={props.ignoredZones} item={element} />
              </div>
            )}
          </For>
        </div>
        <LastDropZone ignoredItems={props.ignoredZones} />
      </Show>
    </div>
  );
};

export default DroppableList;
